import get from 'lodash/get';
import http from 'utils/http';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { getApiErrorMessage } from 'utils/http/get-api-error-message';
import { getEnvironmentSettings } from 'services/environment';
import { INVENTORY_FORECAST_CACHE_ALLOW } from 'pages/prediction-editor/constants';
import { showEstimateData } from 'states/resources/inventory-predictions/business-logic';
/** import single application imports in the section above, Sort Lines Ascending */

const queryInventoryPrediction = async query => {
    const BAD_REQUEST = 400;
    const MAX_RETRIES = 0;
    const URL_PATH = 'inventory-predictions';

    let lastError;

    for (let retryCount = 0; retryCount <= MAX_RETRIES; retryCount++) {
        try {
            const response = await http().post(URL_PATH, query);
            return response; // If successful, return the response immediately
        } catch (err) {
            if (err?.code === BAD_REQUEST) {
                throw err;
            }
            lastError = err;
        }
    }

    console.error('GET inventory prediction error', lastError);
    throw lastError;
};

const getGISAuthToken = async (cacheStrategy = INVENTORY_FORECAST_CACHE_ALLOW) => {
    const URL_PATH = 'inventory-predictions/auth-gis';
    try {
        const response = await http().get(URL_PATH, {
            cache_strategy: cacheStrategy,
        });
        return response; // If successful, return the response immediately
    } catch (err) {
        console.error('GET auth-gis request error', err);
        throw err;
    }
};

const actions = {
    getGISAuthToken(cacheStrategy) {
        return async dispatch => {
            dispatch({
                type: 'INVENTORY_PREDICTIONS__GET_GIS_TOKEN',
            });
            try {
                const response = await getGISAuthToken(cacheStrategy);
                dispatch({
                    type: 'INVENTORY_PREDICTIONS__GET_GIS_TOKEN__COMPLETE',
                    payload: {
                        token: response,
                    },
                });
            } catch (err) {
                console.error('GET auth-gis response error', err);
                dispatch({
                    type: 'INVENTORY_PREDICTIONS__GET_GIS_TOKEN__FAILED',
                    payload: {
                        error: 'Map failed to load. Please try refreshing the page.',
                    },
                });
                throw err;
            }
        };
    },

    fetch(query) {
        return () => {
            return queryInventoryPrediction(query);
        };
    },
    /**
     * Validates the forecast form before calling the API.
     *
     * @param {object} variables - Draft fields from the prediction editor (includes daysRunning).
     * @param {object} options
     * @param {string} options.reportingDatasource - 'snowflake' or 'elasticsearch' (from editor state).
     */
    validatePredictionForm(variables, { reportingDatasource }) {
        const useSnowflake = reportingDatasource === 'snowflake';
        const isDOOH = useSnowflake && variables.platforms.includes('dooh');
        return async (dispatch, getState) => {
            const validations = {
                daysRunning: {
                    error: 'Set days running to see forecast (at least 1 day is required)',
                    isValid: value => parseInt(value) > 0,
                },
                'custom.creativeFormat': {
                    error: 'Select a creative format to see forecast',
                    isValid: () => variables.creativeFormat || isDOOH,
                },
                creativeSize: {
                    error: 'Select a creative size to see forecast',
                    isValid: creativeSize => creativeSize || isDOOH,
                },
                'custom.creativeType': {
                    error: 'Select a creative type to see forecast',
                    isValid: () => (variables.creativeType && variables.creativeFormat) || isDOOH,
                },
                geotargets: {
                    error: 'Select a country to see forecast',
                    isValid: geotargets => geotargets.length > 0,
                },
                'custom.audiences': {
                    error: 'Forecast not supported for custom segments',
                    isValid: () => {
                        const allAudiences = _.get(getState(), 'resources.audiences', {});

                        const isUsingNonPdsIncludeSegments = _(variables.audiences)
                            .map(
                                audienceId =>
                                    allAudiences[audienceId] && allAudiences[audienceId].attr
                            )
                            .some(audience => audience.source_type.indexOf('pds_') === -1);

                        const isUsingNonPdsExcludeSegments = _(variables.audience_exclude)
                            .map(
                                audienceId =>
                                    allAudiences[audienceId] && allAudiences[audienceId].attr
                            )
                            .some(audience => audience.source_type.indexOf('pds_') === -1);

                        return !isUsingNonPdsIncludeSegments && !isUsingNonPdsExcludeSegments;
                    },
                },
                max_cpm_rate_local: {
                    error: 'Set Total Bid Price to see forecast',
                    isValid: max_cpm_rate_local =>
                        (max_cpm_rate_local && parseFloat(max_cpm_rate_local) > 0) || isDOOH,
                },
                bidRate: {
                    error: 'Adjust the Net Bid Price, it should be greater than zero',
                    isValid: bidRate => (bidRate && parseFloat(bidRate) > 0) || isDOOH,
                },
                'fcaps.imp': {
                    error: 'Set Frequency cap to see forecast',
                    isValid: imp => imp,
                },
                'custom.fcaps.interval_unit_1': {
                    error: 'Forecast not supported for hourly frequency cap',
                    isValid: () => variables.fcaps.interval_unit !== 'hours',
                },
                'custom.fcaps.interval_unit_2': {
                    error: 'Forecast not supported for weekly frequency cap',
                    isValid: () => variables.fcaps.interval_unit !== 'weeks',
                },
                'custom.fcaps.interval_unit_3': {
                    error: 'Forecast not supported for frequency caps based on ad lifetime',
                    isValid: () => variables.fcaps.interval_unit !== 'ad',
                },
            };

            for (const [key, value] of Object.entries(validations)) {
                if (!value.isValid(get(variables, key))) {
                    dispatch({
                        type: `INVENTORY_PREDICTIONS__GENERATE_PREDICTION__FAILED`,
                        payload: { error: value.error },
                    });
                    return false;
                }
            }

            return true;
        };
    },
    /**
     * POSTs to inventory-predictions and maps the response for the UI.
     *
     * @param {object} variables - Draft fields from the prediction editor.
     * @param {number} daysRunning - Campaign duration in days.
     * @param {object} options
     * @param {string} options.reportingDatasource - 'snowflake' or 'elasticsearch'; controls payload and response field mapping.
     */
    updatePrediction(variables, daysRunning, { reportingDatasource }) {
        return async dispatch => {
            const useSnowflake = reportingDatasource === 'snowflake';

            dispatch({
                type: 'INVENTORY_PREDICTIONS__GENERATE_PREDICTION',
                payload: {},
            });
            const envSettings = getEnvironmentSettings();

            const requestPayload = {
                geo_cregion_include: _(variables.geotargets)
                    .map(target => target.include)
                    .flatten()
                    .filter(includedTarget => !includedTarget.city && !includedTarget.fsa)
                    .map(includedTarget => includedTarget.region)
                    .value(),
                geo_cregion_exclude: _(variables.geotargets)
                    .map(target => target.exclude)
                    .flatten()
                    .filter(includedTarget => !includedTarget.city && !includedTarget.fsa)
                    .map(includedTarget => includedTarget.region)
                    .value(),
                geo_city_include: _(variables.geotargets)
                    .map(target => target.include)
                    .flatten()
                    .filter(includedTarget => includedTarget.city)
                    .map(includedTarget => includedTarget.city)
                    .value(),
                geo_city_exclude: _(variables.geotargets)
                    .map(target => target.exclude)
                    .flatten()
                    .filter(includedTarget => includedTarget.city)
                    .map(includedTarget => includedTarget.city)
                    .value(),
                geo_zip_include: _(variables.geotargets)
                    .map(target => target.include)
                    .flatten()
                    .filter(includedTarget => includedTarget.fsa)
                    .map(includedTarget => includedTarget.fsa)
                    .value(),
                geo_zip_exclude: _(variables.geotargets)
                    .map(target => target.exclude)
                    .flatten()
                    .filter(includedTarget => includedTarget.fsa)
                    .map(includedTarget => includedTarget.fsa)
                    .value(),
                creative_size: variables.creativeSize,
                creative_format: variables.creativeFormat,
                creative_type: variables.creativeType,
                age_groups: variables.target_age_groups,
                genders: variables.target_genders,
                device_os: variables.target_device_os,
                frequency_cap: variables.fcaps.imp,
                bid_rate: variables.max_cpm_rate_local,
                ttype: variables.platforms,
                target_device_language: variables.target_device_language,
                target_device_language_exclude: variables.target_device_language_exclude,
                whitelistedAppsAndSites: variables.whitelistedAppsAndSites,
                blacklistedAppsAndSites: variables.blacklistedAppsAndSites,
                target_video_skippable: parseInt(variables.target_video_skippable),
                placements: variables.placements,
                positions: variables.positions,
                currency: envSettings.getInventoryForecastCurrency(),
                target_boards: variables.target_boards,
                target_boards_exclude: variables.target_boards_exclude,
                target_dooh_venues: variables.target_dooh_venues,
                target_dooh_venues_exclude: variables.target_dooh_venues_exclude,
                cache_strategy: variables.cache_strategy,
            };

            requestPayload.bid_rate = parseFloat(variables.bidRate.toFixed(2));
            requestPayload.pattison_billboards = variables.pattisonBillboards;

            requestPayload.tactics_generators = variables.tactics_generators;
            requestPayload.days_running = daysRunning;

            try {
                if (requestPayload.ttype.includes('dooh')) {
                    const resultsDooh = await queryInventoryPrediction({
                        ...requestPayload,
                    });
                    dispatch({
                        type: 'INVENTORY_PREDICTIONS__GENERATE_PREDICTION__COMPLETE',
                        payload: {
                            data: resultsDooh.data,
                            inventoryDataIngestedAt: resultsDooh.ingested_at,
                        },
                    });
                    return;
                }
                // request 1
                // ctr off will include all possible inventory
                const promiseMax = queryInventoryPrediction({
                    ...requestPayload,
                    ctr: [],
                });

                // request 2
                // ctr on will only include average pool of inventory
                // for video and native, we know it is going to return 0 and won't be used in the UI,
                // so we ignore and don't send a HTTP request to save resources
                const promiseMin = !showEstimateData(variables.creativeFormat)
                    ? Promise.resolve(
                          useSnowflake
                              ? { unique_users: 0, available_impressions: 0 }
                              : { dailyUniqueUsers: 0, dailyAvailableImpressions: 0 }
                      )
                    : queryInventoryPrediction({
                          ...requestPayload,
                          ctr: [1],
                      });

                const [requestMax, requestMin] = await Promise.all([promiseMax, promiseMin]);

                if (useSnowflake) {
                    // Snowflake response shape (unique_users / available_impressions).
                    dispatch({
                        type: 'INVENTORY_PREDICTIONS__GENERATE_PREDICTION__COMPLETE',
                        payload: {
                            dailyUniqueUsers: requestMin.unique_users,
                            dailyAvailableImpressions: requestMin.available_impressions,

                            totalUniqueUsersMin: requestMin.unique_users,
                            totalAvailableImpressionsMin: requestMin.available_impressions,

                            totalUniqueUsersMax: requestMax.unique_users,
                            totalAvailableImpressionsMax: requestMax.available_impressions,
                            inventoryDataIngestedAt: requestMax.ingested_at,
                        },
                    });
                    return;
                }

                // Elasticsearch response shape (dailyUniqueUsers / dailyAvailableImpressions).
                dispatch({
                    type: 'INVENTORY_PREDICTIONS__GENERATE_PREDICTION__COMPLETE',
                    payload: {
                        dailyUniqueUsers: requestMin.dailyUniqueUsers,
                        dailyAvailableImpressions:
                            requestMin.dailyAvailableImpressions * daysRunning,

                        totalUniqueUsersMin: requestMin.dailyUniqueUsers,
                        totalAvailableImpressionsMin:
                            requestMin.dailyAvailableImpressions * daysRunning,

                        totalUniqueUsersMax: requestMax.dailyUniqueUsers,
                        totalAvailableImpressionsMax:
                            requestMax.dailyAvailableImpressions * daysRunning,
                    },
                });
            } catch (err) {
                dispatch({
                    type: 'INVENTORY_PREDICTIONS__GENERATE_PREDICTION__FAILED',
                    payload: {
                        error: getApiErrorMessage(err, 'An unknown error occurred'),
                    },
                });
            }
        };
    },
};

export default actions;
